import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Building2, Plus, CreditCard, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', code: '', area: '', address: '', phone: '', bank_name: '', account_number: '', account_holder: '' };

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data } = await api.get('/locations');
      setLocations(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditMode(false);
    setEditingCode(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (loc) => {
    setEditMode(true);
    setEditingCode(loc.code);
    setForm({
      name: loc.name || '',
      code: loc.code || '',
      area: loc.area || '',
      address: loc.address || '',
      phone: loc.phone || '',
      bank_name: loc.bank_name || '',
      account_number: loc.account_number || '',
      account_holder: loc.account_holder || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Hapus cabang "${loc.name}" (${loc.code})? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/locations/${loc.code}`);
      toast.success('Cabang berhasil dihapus');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus cabang');
    }
  };

  const handleSave = async () => {
    const code = (form.code || '').trim();
    if (!code || !/^[A-Za-z0-9_-]+$/.test(code)) {
        toast.error('Kode lokasi wajib diisi, tanpa spasi (hanya huruf, angka, - dan _)');
        return;
    }
    try {
        if (editMode) {
            const { code: _unused, ...updateData } = form;
            await api.put(`/locations/${editingCode}`, updateData);
            toast.success('Lokasi berhasil diperbarui');
        } else {
            await api.post('/locations', { ...form, code });
            toast.success('Lokasi berhasil ditambahkan');
        }
        setShowDialog(false);
        setForm(emptyForm);
        loadData();
    } catch (error) {
        toast.error(error.response?.data?.detail || 'Gagal menyimpan lokasi');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="locations-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Master Data</p>
            <h1 className="text-4xl font-medium tracking-tight">Lokasi Cabang</h1>
            <p className="text-muted-foreground mt-1">Kelola cabang hotel & info rekening bank</p>
          </div>
          <Button
            onClick={openAdd}
            className="bg-primary hover:bg-primary/90"
            data-testid="add-location-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah Cabang
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((loc) => (
              <Card
                key={loc.id}
                className="border-border hover:-translate-y-1 transition-transform"
                data-testid={`location-${loc.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex items-center gap-2">
                      {loc.is_warehouse && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">
                          Gudang
                        </Badge>
                      )}
                      <Badge variant="outline" className="font-mono">{loc.id}</Badge>
                    </div>
                  </div>
                  <h3 className="font-medium tracking-tight text-lg mb-2">{loc.name}</h3>
                  {loc.area && (
                    <Badge variant="secondary" className="text-xs mb-2">{loc.area}</Badge>
                  )}
                  {loc.address && <p className="text-sm text-muted-foreground mb-1">{loc.address}</p>}
                  {loc.phone && <p className="text-sm text-muted-foreground mb-3">{loc.phone}</p>}

                  {loc.bank_name ? (
                    <div className="pt-3 mt-3 border-t border-border">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-muted-foreground" />
                        <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Rekening Petty Cash</p>
                      </div>
                      <p className="text-sm font-medium">{loc.bank_name}</p>
                      <p className="text-sm font-mono">{loc.account_number}</p>
                      <p className="text-xs text-muted-foreground">a.n. {loc.account_holder}</p>
                    </div>
                  ) : (
                    !loc.is_warehouse && (
                      <div className="pt-3 mt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground italic">Belum ada info rekening</p>
                      </div>
                    )
                  )}

                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEdit(loc)}
                      data-testid={`edit-location-${loc.id}`}
                    >
                      <Edit className="w-3 h-3 mr-2" />
                      Edit
                    </Button>
                    {loc.code !== 'PUSAT' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(loc)}
                        className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        data-testid={`delete-location-${loc.id}`}
                        title="Hapus cabang"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Lokasi' : 'Tambah Cabang Hotel'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label>Nama Cabang *</Label>
                  <Input
                    placeholder="Hotel Tondano"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="location-name-input"
                  />
                </div>
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label>Kode Cabang *</Label>
                  <Input
                    placeholder="TDN"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    disabled={editMode}
                    data-testid="location-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Area</Label>
                <Input
                  placeholder="Jawa Timur / Sulawesi Utara, dll"
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                  data-testid="location-area-input"
                />
                <p className="text-xs text-muted-foreground">Grup wilayah cabang ini, dipakai untuk filter Area di halaman lain.</p>
              </div>
              <div className="space-y-2">
                <Label>Alamat</Label>
                <Input
                  placeholder="Jl. Contoh No. 123"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="location-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Telepon</Label>
                <Input
                  placeholder="021-12345678"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="location-phone-input"
                />
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold">Rekening Petty Cash Cabang</p>
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Nama Bank</Label>
                    <Input
                      placeholder="Bank Mandiri / BCA / BNI, dll"
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      data-testid="location-bank-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nomor Rekening</Label>
                    <Input
                      placeholder="1234567890"
                      value={form.account_number}
                      onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                      data-testid="location-account-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nama Pemilik Rekening</Label>
                    <Input
                      placeholder="PT Hotel Tondano"
                      value={form.account_holder}
                      onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                      data-testid="location-account-holder-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
              <Button onClick={handleSave} className="bg-primary hover:bg-primary/90" data-testid="save-location-button">
                {editMode ? 'Simpan Perubahan' : 'Simpan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default LocationsPage;
